import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cvAPI, aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CVPreviewModal from '../components/CVPreviewModal';
import UpgradeModal from '../components/UpgradeModal';
import { FormFieldSkeleton } from '../components/ui/skeleton-loaders';
import { 
  FileText, ChevronLeft, ChevronRight, Check, Loader2, 
  Plus, Trash2, Sparkles, Save, Eye, Download, X, AlertCircle, Palette,
  CircleCheck, Circle, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const CVBuilderPage = () => {
  const { id: cvId } = useParams();
  const navigate = useNavigate();
  const { isSubscribed } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState('default');

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const [cvData, setCvData] = useState({
    cv_type: 'entry_level',
    template_style: 'classic',
    title: '',
    personal_details: {
      full_name: '',
      email: '',
      phone: '',
      city: '',
      province: '',
      linkedin: '',
      profile_photo: '',
      drivers_license: '',
      own_transport: false,
      availability: '',
      date_of_birth: '',
      gender: '',
      nationality: '',
      health_status: '',
      criminal_record: '',
      willingness_to_travel: '',
      dependants: ''
    },
    professional_summary: '',
    education: [],
    work_experience: [],
    skills: [],
    languages: [],
    certifications: [],
    professional_memberships: [],
    references: [],
    accomplishments: [],
    hobbies: []
  });

  const steps = [
    { name: 'CV Type', description: 'Choose your CV template' },
    { name: 'Personal Details', description: 'Your contact information' },
    { name: 'Work Experience', description: 'Employment history' },
    { name: 'Education', description: 'Academic background' },
    { name: 'Skills', description: 'Your abilities' },
    { name: 'Languages', description: 'Language proficiency' },
    { name: 'Summary', description: 'Professional summary' },
    { name: 'Extras', description: 'Certifications & references' }
  ];

  const cvTypes = [
    { value: 'no_experience', label: 'No Experience', desc: 'For first-time job seekers' },
    { value: 'entry_level', label: 'Entry Level', desc: 'For graduates and interns' },
    { value: 'experienced', label: 'Experienced', desc: 'For professionals with work history' },
    { value: 'career_switcher', label: 'Career Switcher', desc: 'For those changing industries' }
  ];

  const templateStyles = [
    { value: 'classic', label: 'Classic', desc: 'Clean and professional', color: '#1e293b', features: ['ATS-friendly', 'Traditional layout', 'Easy to read'] },
    { value: 'modern', label: 'Modern', desc: 'Contemporary with color accents', color: '#0ea5e9', features: ['ATS-friendly', 'Modern typography', 'Visual appeal'] },
    { value: 'executive', label: 'Executive', desc: 'Sophisticated for senior roles', color: '#059669', features: ['ATS-friendly', 'Premium feel', 'Authoritative'] },
    { value: 'creative', label: 'Creative', desc: 'Bold and distinctive', color: '#8b5cf6', features: ['ATS-friendly', 'Bold design', 'Stand out'] },
    { value: 'minimal', label: 'Minimal', desc: 'Ultra-clean, text-focused', color: '#18181b', features: ['Maximum ATS', 'Clean layout', 'No distractions'] },
    { value: 'technical', label: 'Technical', desc: 'Skills-first for IT/Engineering', color: '#dc2626', features: ['ATS-friendly', 'Skills prominent', 'Tech-focused'] }
  ];

  const provinces = [
    'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape', 
    'Free State', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape'
  ];

  useEffect(() => {
    if (cvId) {
      loadCV();
    }
  }, [cvId]);

  const loadCV = async () => {
    setLoading(true);
    try {
      const response = await cvAPI.get(cvId);
      setCvData(response.data);
    } catch (error) {
      toast.error('Failed to load CV');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (section, field, value) => {
    if (field) {
      setCvData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setCvData(prev => ({ ...prev, [section]: value }));
    }
  };

  const addItem = (section, template) => {
    const newId = Date.now().toString();
    setCvData(prev => ({
      ...prev,
      [section]: [...(prev[section] || []), { ...template, id: newId }]
    }));
    // Auto-scroll to the new item after render
    setTimeout(() => {
      const el = document.getElementById(`item-${newId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const updateItem = (section, id, field, value) => {
    setCvData(prev => ({
      ...prev,
      [section]: (prev[section] || []).map(item => 
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItem = (section, id) => {
    setCvData(prev => ({
      ...prev,
      [section]: (prev[section] || []).filter(item => item.id !== id)
    }));
  };

  const handleEnhance = async (text, context, callback) => {
    if (!text.trim()) return;
    setEnhancing(true);
    try {
      const response = await aiAPI.enhance(text, context);
      callback(response.data.enhanced_text);
      toast.success('Text enhanced!');
    } catch (error) {
      toast.error('Enhancement failed. Try again.');
    } finally {
      setEnhancing(false);
    }
  };

  // Calculate CV Completeness Score
  const calculateCompleteness = () => {
    const checks = [
      { name: 'Full Name', done: !!cvData.personal_details?.full_name?.trim(), required: true },
      { name: 'Email', done: !!cvData.personal_details?.email?.trim(), required: true },
      { name: 'Phone', done: !!cvData.personal_details?.phone?.trim(), required: true },
      { name: 'City', done: !!cvData.personal_details?.city?.trim(), required: true },
      { name: 'Province', done: !!cvData.personal_details?.province?.trim(), required: false },
      { name: 'Professional Summary', done: cvData.professional_summary?.length >= 50, required: true },
      { name: 'Work Experience', done: cvData.work_experience?.length > 0 || cvData.cv_type === 'no_experience', required: true },
      { name: 'Education', done: cvData.education?.length > 0, required: true },
      { name: 'Skills', done: cvData.skills?.length >= 3, required: true },
      { name: 'Languages', done: cvData.languages?.length > 0, required: false },
      { name: 'Profile Photo', done: !!cvData.personal_details?.profile_photo, required: false },
      { name: 'LinkedIn Profile', done: !!cvData.personal_details?.linkedin?.trim(), required: false },
    ];

    const requiredChecks = checks.filter(c => c.required);
    const optionalChecks = checks.filter(c => !c.required);
    const requiredDone = requiredChecks.filter(c => c.done).length;
    const optionalDone = optionalChecks.filter(c => c.done).length;
    
    // Required items worth 80%, optional worth 20%
    const requiredScore = (requiredDone / requiredChecks.length) * 80;
    const optionalScore = (optionalDone / optionalChecks.length) * 20;
    const totalScore = Math.round(requiredScore + optionalScore);

    return { 
      score: totalScore, 
      checks, 
      requiredDone, 
      requiredTotal: requiredChecks.length,
      optionalDone,
      optionalTotal: optionalChecks.length
    };
  };

  const completeness = calculateCompleteness();

  const handleTranslateExperience = async (text, callback) => {
    if (!text.trim()) return;
    setEnhancing(true);
    try {
      const response = await aiAPI.translateExperience(text);
      callback(response.data.translated_text);
      toast.success('Experience translated!');
    } catch (error) {
      toast.error('Translation failed. Try again.');
    } finally {
      setEnhancing(false);
    }
  };

  const saveCV = async (redirectToDashboard = false) => {
    setSaving(true);
    try {
      if (cvId) {
        await cvAPI.update(cvId, cvData);
        if (redirectToDashboard) {
          toast.success('CV saved successfully! 🎉');
          navigate('/dashboard', { state: { cvSaved: true } });
        } else {
          toast.success('CV updated!');
        }
      } else {
        const response = await cvAPI.create(cvData);
        if (redirectToDashboard) {
          toast.success('CV created successfully! 🎉');
          navigate('/dashboard', { state: { cvSaved: true } });
        } else {
          toast.success('CV created!');
          navigate(`/cv-builder/${response.data.id}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save CV');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!cvId) {
      await saveCV();
      return;
    }
    try {
      const response = await cvAPI.download(cvId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${cvData.personal_details?.full_name?.replace(' ', '_') || 'CV'}_CV.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CV downloaded!');

      // Show upgrade modal to free users after download
      if (!isSubscribed) {
        setTimeout(() => {
          setUpgradeTrigger('watermark');
          setUpgradeOpen(true);
        }, 1500);
      }
    } catch (error) {
      toast.error('Failed to download CV');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header Skeleton */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 h-16">
          <div className="max-w-5xl mx-auto px-4 h-full flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-200 rounded animate-pulse"></div>
              <div className="h-5 w-32 bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="flex gap-2">
              <div className="w-24 h-10 bg-slate-200 rounded animate-pulse"></div>
              <div className="w-24 h-10 bg-slate-200 rounded animate-pulse"></div>
            </div>
          </div>
        </header>

        {/* Progress Bar Skeleton */}
        <div className="fixed top-16 left-0 right-0 z-40 bg-white border-b border-slate-200 py-4">
          <div className="max-w-5xl mx-auto px-4">
            <div className="flex justify-between items-center">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-8 h-8 bg-slate-200 rounded-full animate-pulse mb-1"></div>
                  <div className="w-16 h-3 bg-slate-200 rounded animate-pulse hidden sm:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form Content Skeleton */}
        <main className="pt-40 pb-24 px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-xl p-6 shadow-sm border border-slate-200">
            <div className="h-7 w-48 bg-slate-200 rounded animate-pulse mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormFieldSkeleton />
              <FormFieldSkeleton />
              <FormFieldSkeleton />
              <FormFieldSkeleton />
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Free users CAN create CVs (up to 1), just get watermarked PDFs
  // Removed subscription block - the backend handles CV limits

  const renderStep = () => {
    switch (currentStep) {
      case 0: // CV Type
        return (
          <div className="space-y-6">
            {/* CV Type Selection */}
            <div>
              <Label className="text-slate-700 font-medium">Choose CV Type</Label>
              <p className="text-sm text-slate-500 mb-4">Select the template that best fits your experience level</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {cvTypes.map((type) => (
                <button
                  key={type.value}
                  data-testid={`cv-type-${type.value}`}
                  onClick={() => updateField('cv_type', null, type.value)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    cvData.cv_type === type.value 
                      ? 'border-slate-900 bg-slate-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-slate-900">{type.label}</span>
                    {cvData.cv_type === type.value && (
                      <div className="w-6 h-6 bg-slate-900 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{type.desc}</p>
                </button>
              ))}
            </div>

            {/* Template Style Selection */}
            <div className="pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <Palette className="w-5 h-5 text-slate-600" />
                <Label className="text-slate-700 font-medium">Choose Template Design</Label>
              </div>
              <p className="text-sm text-slate-500 mb-4">Select a visual style for your CV PDF</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {templateStyles.map((style) => (
                  <button
                    key={style.value}
                    data-testid={`template-${style.value}`}
                    onClick={() => updateField('template_style', null, style.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      cvData.template_style === style.value 
                        ? 'border-2 bg-slate-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    style={{ borderColor: cvData.template_style === style.value ? style.color : undefined }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: style.color }}
                        />
                        <span className="font-semibold text-slate-900">{style.label}</span>
                      </div>
                      {cvData.template_style === style.value && (
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: style.color }}
                        >
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{style.desc}</p>
                    <div className="flex flex-wrap gap-1">
                      {style.features.map((feature, i) => (
                        <span 
                          key={i} 
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: `${style.color}15`, color: style.color }}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Label htmlFor="cv-title" className="text-slate-700 font-medium">CV Title (optional)</Label>
              <Input
                id="cv-title"
                data-testid="cv-title-input"
                value={cvData.title}
                onChange={(e) => updateField('title', null, e.target.value)}
                placeholder="e.g., Marketing Manager CV, Software Developer CV"
                className="mt-1.5 h-12"
              />
            </div>
          </div>
        );

      case 1: // Personal Details
        return (
          <div className="space-y-5">
            {/* Profile Photo Upload */}
            <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-xl">
              <div className="relative">
                {cvData.personal_details.profile_photo ? (
                  <img 
                    src={cvData.personal_details.profile_photo} 
                    alt="Profile" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center text-slate-400">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <Label className="text-slate-700 font-medium">Profile Photo (optional)</Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">Recommended for Modern template. Not required for ATS.</p>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error('Photo must be less than 2MB');
                            return;
                          }
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            updateField('personal_details', 'profile_photo', reader.result);
                            toast.success('Photo uploaded!');
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <span className="inline-flex items-center px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                      Upload Photo
                    </span>
                  </label>
                  {cvData.personal_details.profile_photo && (
                    <button
                      onClick={() => {
                        updateField('personal_details', 'profile_photo', '');
                        toast.success('Photo removed');
                      }}
                      className="px-3 py-1.5 text-red-600 text-sm hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="full-name" className="text-slate-700 font-medium">Full Name *</Label>
                <Input
                  id="full-name"
                  data-testid="full-name-input"
                  value={cvData.personal_details.full_name}
                  onChange={(e) => updateField('personal_details', 'full_name', e.target.value)}
                  placeholder="John Doe"
                  className="mt-1.5 h-12"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={cvData.personal_details.email}
                  onChange={(e) => updateField('personal_details', 'email', e.target.value)}
                  placeholder="john@example.com"
                  className="mt-1.5 h-12"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number *</Label>
                <Input
                  id="phone"
                  data-testid="phone-input"
                  value={cvData.personal_details.phone}
                  onChange={(e) => updateField('personal_details', 'phone', e.target.value)}
                  placeholder="072 123 4567"
                  className="mt-1.5 h-12"
                />
              </div>
              <div>
                <Label htmlFor="city" className="text-slate-700 font-medium">City *</Label>
                <Input
                  id="city"
                  data-testid="city-input"
                  value={cvData.personal_details.city}
                  onChange={(e) => updateField('personal_details', 'city', e.target.value)}
                  placeholder="Johannesburg"
                  className="mt-1.5 h-12"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="province" className="text-slate-700 font-medium">Province</Label>
                <Select 
                  value={cvData.personal_details.province} 
                  onValueChange={(value) => updateField('personal_details', 'province', value)}
                >
                  <SelectTrigger data-testid="province-select" className="mt-1.5 h-12">
                    <SelectValue placeholder="Select province" />
                  </SelectTrigger>
                  <SelectContent>
                    {provinces.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="linkedin" className="text-slate-700 font-medium">LinkedIn (optional)</Label>
                <Input
                  id="linkedin"
                  data-testid="linkedin-input"
                  value={cvData.personal_details.linkedin}
                  onChange={(e) => updateField('personal_details', 'linkedin', e.target.value)}
                  placeholder="linkedin.com/in/yourprofile"
                  className="mt-1.5 h-12"
                />
              </div>
            </div>

            {/* SA-Specific Fields */}
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <h4 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                SA Employer Requirements
              </h4>
              
              {/* Row 1: Personal Info */}
              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label htmlFor="date-of-birth" className="text-slate-700 font-medium">Date of Birth</Label>
                  <Input
                    id="date-of-birth"
                    type="date"
                    data-testid="date-of-birth-input"
                    value={cvData.personal_details.date_of_birth || ''}
                    onChange={(e) => updateField('personal_details', 'date_of_birth', e.target.value)}
                    className="mt-1.5 h-12 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="gender" className="text-slate-700 font-medium">Gender</Label>
                  <Select 
                    value={cvData.personal_details.gender || ''} 
                    onValueChange={(value) => updateField('personal_details', 'gender', value)}
                  >
                    <SelectTrigger data-testid="gender-select" className="mt-1.5 h-12 bg-white">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                      <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="nationality" className="text-slate-700 font-medium">Nationality</Label>
                  <Select 
                    value={cvData.personal_details.nationality || ''} 
                    onValueChange={(value) => updateField('personal_details', 'nationality', value)}
                  >
                    <SelectTrigger data-testid="nationality-select" className="mt-1.5 h-12 bg-white">
                      <SelectValue placeholder="Select nationality" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="South African">South African</SelectItem>
                      <SelectItem value="Permanent Resident">Permanent Resident</SelectItem>
                      <SelectItem value="Work Permit Holder">Work Permit Holder</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Work Requirements */}
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="drivers-license" className="text-slate-700 font-medium">Driver's License</Label>
                  <Select 
                    value={cvData.personal_details.drivers_license || ''} 
                    onValueChange={(value) => updateField('personal_details', 'drivers_license', value)}
                  >
                    <SelectTrigger data-testid="drivers-license-select" className="mt-1.5 h-12 bg-white">
                      <SelectValue placeholder="Select code" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="Code A">Code A (Motorcycles)</SelectItem>
                      <SelectItem value="Code B">Code B (Light vehicles)</SelectItem>
                      <SelectItem value="Code C">Code C (Heavy vehicles)</SelectItem>
                      <SelectItem value="Code C1">Code C1 (3,500-16,000kg)</SelectItem>
                      <SelectItem value="Code EC">Code EC (Articulated)</SelectItem>
                      <SelectItem value="Code EC1">Code EC1 (Articulated light)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Own Transport</Label>
                  <div className="mt-1.5 flex gap-3">
                    <button
                      type="button"
                      data-testid="own-transport-yes"
                      onClick={() => updateField('personal_details', 'own_transport', true)}
                      className={`flex-1 h-12 rounded-lg border-2 font-medium transition-all ${
                        cvData.personal_details.own_transport 
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      data-testid="own-transport-no"
                      onClick={() => updateField('personal_details', 'own_transport', false)}
                      className={`flex-1 h-12 rounded-lg border-2 font-medium transition-all ${
                        !cvData.personal_details.own_transport 
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="availability" className="text-slate-700 font-medium">Availability / Notice Period</Label>
                  <Select 
                    value={cvData.personal_details.availability || ''} 
                    onValueChange={(value) => updateField('personal_details', 'availability', value)}
                  >
                    <SelectTrigger data-testid="availability-select" className="mt-1.5 h-12 bg-white">
                      <SelectValue placeholder="Select availability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Immediately">Immediately</SelectItem>
                      <SelectItem value="1 week">1 week notice</SelectItem>
                      <SelectItem value="2 weeks">2 weeks notice</SelectItem>
                      <SelectItem value="1 month">1 month notice</SelectItem>
                      <SelectItem value="2 months">2 months notice</SelectItem>
                      <SelectItem value="3 months">3 months notice</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 4: Additional SA Requirements */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                <div>
                  <Label htmlFor="health-status" className="text-slate-700 font-medium">Health Status</Label>
                  <Select 
                    value={cvData.personal_details.health_status || ''} 
                    onValueChange={(value) => updateField('personal_details', 'health_status', value)}
                  >
                    <SelectTrigger data-testid="health-status-select" className="mt-1.5 h-12 bg-white">
                      <SelectValue placeholder="Select health status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Good">Good health</SelectItem>
                      <SelectItem value="Excellent">Excellent health</SelectItem>
                      <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="criminal-record" className="text-slate-700 font-medium">Criminal Record</Label>
                  <Select 
                    value={cvData.personal_details.criminal_record || ''} 
                    onValueChange={(value) => updateField('personal_details', 'criminal_record', value)}
                  >
                    <SelectTrigger data-testid="criminal-record-select" className="mt-1.5 h-12 bg-white">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="None">No criminal record</SelectItem>
                      <SelectItem value="Cleared">Cleared / Expunged</SelectItem>
                      <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="travel" className="text-slate-700 font-medium">Willingness to Travel</Label>
                  <Select 
                    value={cvData.personal_details.willingness_to_travel || ''} 
                    onValueChange={(value) => updateField('personal_details', 'willingness_to_travel', value)}
                  >
                    <SelectTrigger data-testid="travel-select" className="mt-1.5 h-12 bg-white">
                      <SelectValue placeholder="Select willingness" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes - Anytime">Yes - Anytime</SelectItem>
                      <SelectItem value="Yes - Up to 25%">Yes - Up to 25% of time</SelectItem>
                      <SelectItem value="Yes - Up to 50%">Yes - Up to 50% of time</SelectItem>
                      <SelectItem value="Yes - Up to 75%">Yes - Up to 75% of time</SelectItem>
                      <SelectItem value="Local only">Local area only</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="dependants" className="text-slate-700 font-medium">Number of Dependants</Label>
                  <Select 
                    value={cvData.personal_details.dependants || ''} 
                    onValueChange={(value) => updateField('personal_details', 'dependants', value)}
                  >
                    <SelectTrigger data-testid="dependants-select" className="mt-1.5 h-12 bg-white">
                      <SelectValue placeholder="Select number" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">None</SelectItem>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="5+">5 or more</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Work Experience
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Work Experience</h3>
                <p className="text-sm text-slate-500">Add your employment history (newest first)</p>
              </div>
              <Button
                data-testid="add-experience-btn"
                onClick={() => addItem('work_experience', {
                  company: '', job_title: '', description: '', 
                  start_date: '', end_date: '', is_current: false, is_informal: false
                })}
                variant="outline"
                className="border-slate-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Experience
              </Button>
            </div>

            {cvData.work_experience.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                <p className="text-slate-500 mb-2">No experience added yet</p>
                <p className="text-sm text-slate-400">Click "Add Experience" to get started</p>
              </div>
            ) : (
              <div className="space-y-6">
                {cvData.work_experience.map((exp, index) => (
                  <div key={exp.id} id={`item-${exp.id}`} className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-medium text-slate-700">Experience {index + 1}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem('work_experience', exp.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id={`informal-${exp.id}`}
                          checked={exp.is_informal}
                          onChange={(e) => updateItem('work_experience', exp.id, 'is_informal', e.target.checked)}
                          className="rounded border-slate-300"
                        />
                        <Label htmlFor={`informal-${exp.id}`} className="text-sm text-slate-600">
                          This is informal/freelance work
                        </Label>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-600 text-sm">Job Title</Label>
                          <Input
                            value={exp.job_title}
                            onChange={(e) => updateItem('work_experience', exp.id, 'job_title', e.target.value)}
                            placeholder="e.g., Sales Assistant"
                            className="mt-1 h-11"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-600 text-sm">Company</Label>
                          <Input
                            value={exp.company}
                            onChange={(e) => updateItem('work_experience', exp.id, 'company', e.target.value)}
                            placeholder="e.g., ABC Retail"
                            className="mt-1 h-11"
                          />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-600 text-sm">Start Date</Label>
                          <Input
                            value={exp.start_date}
                            onChange={(e) => updateItem('work_experience', exp.id, 'start_date', e.target.value)}
                            placeholder="Jan 2022"
                            className="mt-1 h-11"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-600 text-sm">End Date</Label>
                          <Input
                            value={exp.end_date}
                            onChange={(e) => updateItem('work_experience', exp.id, 'end_date', e.target.value)}
                            placeholder="Dec 2023"
                            disabled={exp.is_current}
                            className="mt-1 h-11"
                          />
                        </div>
                        <div className="flex items-end pb-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={exp.is_current}
                              onChange={(e) => updateItem('work_experience', exp.id, 'is_current', e.target.checked)}
                              className="rounded border-slate-300"
                            />
                            <span className="text-sm text-slate-600">Currently working here</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-slate-600 text-sm">Description</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={enhancing || !exp.description}
                            onClick={() => {
                              if (exp.is_informal) {
                                handleTranslateExperience(exp.description, (text) => 
                                  updateItem('work_experience', exp.id, 'description', text)
                                );
                              } else {
                                handleEnhance(exp.description, 'experience', (text) => 
                                  updateItem('work_experience', exp.id, 'description', text)
                                );
                              }
                            }}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 animate-pulse bg-amber-50 border border-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.4)]"
                          >
                            {enhancing ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Sparkles className="w-4 h-4 mr-1" />
                            )}
                            {exp.is_informal ? '✨ AI Translate' : '✨ AI Enhance'}
                          </Button>
                        </div>
                        <Textarea
                          value={exp.description}
                          onChange={(e) => updateItem('work_experience', exp.id, 'description', e.target.value)}
                          placeholder={exp.is_informal 
                            ? "Describe what you did informally (e.g., 'Helped at family shop')" 
                            : "Describe your responsibilities and achievements"
                          }
                          className="min-h-[100px]"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bottom add button - no scrolling needed */}
            {cvData.work_experience.length > 0 && (
              <Button
                data-testid="add-experience-bottom-btn"
                onClick={() => addItem('work_experience', {
                  company: '', job_title: '', description: '', 
                  start_date: '', end_date: '', is_current: false, is_informal: false
                })}
                variant="outline"
                className="w-full border-dashed border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Another Experience
              </Button>
            )}
          </div>
        );

      case 3: // Education
        return (
          <div className="space-y-6">
            {/* Reassuring message */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>No degree? No problem!</strong> Many employers value skills and experience over formal qualifications. 
                Add your highest level of schooling, even if it's Grade 12 or incomplete.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Education</h3>
                <p className="text-sm text-slate-500">Add your schooling or qualifications</p>
              </div>
              <Button
                data-testid="add-education-btn"
                onClick={() => addItem('education', {
                  institution: '', qualification: '', field_of_study: '',
                  start_year: '', end_year: '', is_current: false
                })}
                variant="outline"
                className="border-slate-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Education
              </Button>
            </div>

            {cvData.education.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                <p className="text-slate-500 mb-2">No education added yet</p>
                <p className="text-sm text-slate-400">Add your matric, school, or any training you've completed</p>
              </div>
            ) : (
              <div className="space-y-6">
                {cvData.education.map((edu, index) => (
                  <div key={edu.id} id={`item-${edu.id}`} className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-medium text-slate-700">Education {index + 1}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem('education', edu.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-600 text-sm">Qualification Level</Label>
                        <Select 
                          value={edu.qualification} 
                          onValueChange={(value) => updateItem('education', edu.id, 'qualification', value)}
                        >
                          <SelectTrigger className="mt-1 h-11">
                            <SelectValue placeholder="Select your qualification" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Grade 12 / Matric">Grade 12 / Matric</SelectItem>
                            <SelectItem value="Grade 11">Grade 11</SelectItem>
                            <SelectItem value="Grade 10">Grade 10</SelectItem>
                            <SelectItem value="Some High School">Some High School</SelectItem>
                            <SelectItem value="Primary School">Primary School</SelectItem>
                            <SelectItem value="National Certificate">National Certificate (NC)</SelectItem>
                            <SelectItem value="N1-N3 Certificate">N1-N3 Certificate</SelectItem>
                            <SelectItem value="N4-N6 Certificate">N4-N6 Certificate</SelectItem>
                            <SelectItem value="Higher Certificate">Higher Certificate</SelectItem>
                            <SelectItem value="Diploma">Diploma</SelectItem>
                            <SelectItem value="Advanced Diploma">Advanced Diploma</SelectItem>
                            <SelectItem value="Bachelor's Degree">Bachelor's Degree</SelectItem>
                            <SelectItem value="Honours Degree">Honours Degree</SelectItem>
                            <SelectItem value="Master's Degree">Master's Degree</SelectItem>
                            <SelectItem value="Doctorate">Doctorate (PhD)</SelectItem>
                            <SelectItem value="Short Course">Short Course / Training</SelectItem>
                            <SelectItem value="Learnership">Learnership</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">School / Institution</Label>
                        <Input
                          value={edu.institution}
                          onChange={(e) => updateItem('education', edu.id, 'institution', e.target.value)}
                          placeholder="e.g., Soweto High School, UNISA, TVET College"
                          className="mt-1 h-11"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Field of Study / Subjects (optional)</Label>
                        <Input
                          value={edu.field_of_study}
                          onChange={(e) => updateItem('education', edu.id, 'field_of_study', e.target.value)}
                          placeholder="e.g., Maths, English, Accounting or Business Administration"
                          className="mt-1 h-11"
                        />
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-600 text-sm">Start Year</Label>
                          <Input
                            value={edu.start_year}
                            onChange={(e) => updateItem('education', edu.id, 'start_year', e.target.value)}
                            placeholder="2018"
                            className="mt-1 h-11"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-600 text-sm">End Year</Label>
                          <Input
                            value={edu.end_year}
                            onChange={(e) => updateItem('education', edu.id, 'end_year', e.target.value)}
                            placeholder="2022"
                            disabled={edu.is_current}
                            className="mt-1 h-11"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-600 text-sm">NQF Level (optional)</Label>
                          <Select
                            value={edu.nqf_level || ''}
                            onValueChange={(v) => updateItem('education', edu.id, 'nqf_level', v)}
                          >
                            <SelectTrigger className="mt-1 h-11">
                              <SelectValue placeholder="Select NQF" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_specified">Not specified</SelectItem>
                              <SelectItem value="NQF 1">NQF 1 - Grade 9</SelectItem>
                              <SelectItem value="NQF 2">NQF 2 - Grade 10</SelectItem>
                              <SelectItem value="NQF 3">NQF 3 - Grade 11</SelectItem>
                              <SelectItem value="NQF 4">NQF 4 - Matric / N3</SelectItem>
                              <SelectItem value="NQF 5">NQF 5 - Higher Cert / N4-N6</SelectItem>
                              <SelectItem value="NQF 6">NQF 6 - Diploma / BTech</SelectItem>
                              <SelectItem value="NQF 7">NQF 7 - Degree</SelectItem>
                              <SelectItem value="NQF 8">NQF 8 - Honours / PGDip</SelectItem>
                              <SelectItem value="NQF 9">NQF 9 - Masters</SelectItem>
                              <SelectItem value="NQF 10">NQF 10 - Doctorate</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-center pt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={edu.is_current}
                            onChange={(e) => updateItem('education', edu.id, 'is_current', e.target.checked)}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm text-slate-600">Currently studying</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bottom add button */}
            {cvData.education.length > 0 && (
              <Button
                data-testid="add-education-bottom-btn"
                onClick={() => addItem('education', {
                  institution: '', qualification: '', field_of_study: '',
                  start_year: '', end_year: '', is_current: false
                })}
                variant="outline"
                className="w-full border-dashed border-slate-300 text-slate-500 hover:text-slate-700 hover:border-slate-400"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Another Qualification
              </Button>
            )}
          </div>
        );

      case 4: // Skills
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Skills</h3>
                <p className="text-sm text-slate-500">List your professional skills</p>
              </div>
              <Button
                data-testid="add-skill-btn"
                onClick={() => addItem('skills', { name: '', level: 'Intermediate' })}
                variant="outline"
                className="border-slate-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Skill
              </Button>
            </div>

            {cvData.skills.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                <p className="text-slate-500 mb-2">No skills added yet</p>
                <p className="text-sm text-slate-400">Click "Add Skill" to get started</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {cvData.skills.map((skill) => (
                  <div key={skill.id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <Input
                      value={skill.name}
                      onChange={(e) => updateItem('skills', skill.id, 'name', e.target.value)}
                      placeholder="e.g., Microsoft Excel"
                      className="flex-1 h-10"
                    />
                    <Select 
                      value={skill.level} 
                      onValueChange={(value) => updateItem('skills', skill.id, 'level', value)}
                    >
                      <SelectTrigger className="w-32 h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Beginner">Beginner</SelectItem>
                        <SelectItem value="Intermediate">Intermediate</SelectItem>
                        <SelectItem value="Advanced">Advanced</SelectItem>
                        <SelectItem value="Expert">Expert</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem('skills', skill.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>Tip:</strong> Only list skills you actually have. Our AI will help match your skills to job requirements, but it will never add skills you don't possess.
              </p>
            </div>
          </div>
        );

      case 5: // Languages
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Languages</h3>
                <p className="text-sm text-slate-500">List languages you speak - important for SA employers</p>
              </div>
              <Button
                data-testid="add-language-btn"
                onClick={() => addItem('languages', { name: '', proficiency: 'Conversational' })}
                variant="outline"
                className="border-slate-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Language
              </Button>
            </div>

            {cvData.languages?.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                <p className="text-slate-500 mb-2">No languages added yet</p>
                <p className="text-sm text-slate-400">Click "Add Language" to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cvData.languages?.map((lang) => (
                  <div key={lang.id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <Select 
                      value={lang.name} 
                      onValueChange={(value) => updateItem('languages', lang.id, 'name', value)}
                    >
                      <SelectTrigger className="w-40 h-10">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="English">English</SelectItem>
                        <SelectItem value="Afrikaans">Afrikaans</SelectItem>
                        <SelectItem value="Zulu">Zulu (isiZulu)</SelectItem>
                        <SelectItem value="Xhosa">Xhosa (isiXhosa)</SelectItem>
                        <SelectItem value="Sotho">Sotho (Sesotho)</SelectItem>
                        <SelectItem value="Tswana">Tswana (Setswana)</SelectItem>
                        <SelectItem value="Pedi">Pedi (Sepedi)</SelectItem>
                        <SelectItem value="Venda">Venda (Tshivenda)</SelectItem>
                        <SelectItem value="Tsonga">Tsonga (Xitsonga)</SelectItem>
                        <SelectItem value="Swati">Swati (siSwati)</SelectItem>
                        <SelectItem value="Ndebele">Ndebele (isiNdebele)</SelectItem>
                        <SelectItem value="French">French</SelectItem>
                        <SelectItem value="Portuguese">Portuguese</SelectItem>
                        <SelectItem value="German">German</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select 
                      value={lang.proficiency} 
                      onValueChange={(value) => updateItem('languages', lang.id, 'proficiency', value)}
                    >
                      <SelectTrigger className="flex-1 h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Native">Native / First Language</SelectItem>
                        <SelectItem value="Fluent">Fluent (speak, read, write)</SelectItem>
                        <SelectItem value="Conversational">Conversational (can communicate)</SelectItem>
                        <SelectItem value="Basic">Basic (limited vocabulary)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem('languages', lang.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>Tip:</strong> South Africa has 11 official languages. Being multilingual is highly valued by employers, especially in customer-facing roles.
              </p>
            </div>
          </div>
        );

      case 6: // Professional Summary
        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-slate-900">Professional Summary</h3>
                  <p className="text-sm text-slate-500">A brief overview of your career and goals</p>
                </div>
                <Button
                  variant="ghost"
                  disabled={enhancing || !cvData.professional_summary}
                  onClick={() => handleEnhance(cvData.professional_summary, 'summary', (text) => 
                    updateField('professional_summary', null, text)
                  )}
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 animate-pulse bg-amber-50 border border-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.4)]"
                >
                  {enhancing ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-1" />
                  )}
                  ✨ AI Enhance (Recommended)
                </Button>
              </div>
              <Textarea
                data-testid="summary-input"
                value={cvData.professional_summary}
                onChange={(e) => updateField('professional_summary', null, e.target.value)}
                placeholder="Write 2-3 sentences about your professional background, key skills, and career objectives..."
                className="min-h-[150px]"
              />
              <p className="text-sm text-slate-400 mt-2">
                {cvData.professional_summary.length}/500 characters recommended
              </p>
            </div>

            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <p className="text-emerald-800 text-sm">
                <strong>AI Promise:</strong> When you click "Enhance with AI", we only improve language and structure. We never add qualifications, skills, or experience that you haven't mentioned.
              </p>
            </div>
          </div>
        );

      case 7: // Extras (Certifications & References)
        return (
          <div className="space-y-8">
            {/* Certifications */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">Certifications & Courses</h3>
                  <p className="text-sm text-slate-500">Add SETA certificates, courses, or other qualifications</p>
                </div>
                <Button
                  data-testid="add-cert-btn"
                  onClick={() => addItem('certifications', { name: '', issuer: '', year: '', unit_standards: '' })}
                  variant="outline"
                  className="border-slate-200"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
              {cvData.certifications.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <p className="text-slate-500 text-sm">No certifications added (optional)</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cvData.certifications.map((cert) => (
                    <div key={cert.id} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="flex items-center gap-3 mb-2">
                        <Input
                          value={cert.name}
                          onChange={(e) => updateItem('certifications', cert.id, 'name', e.target.value)}
                          placeholder="Certificate name (e.g., National Certificate: IT Support)"
                          className="flex-1 h-10"
                        />
                        <Input
                          value={cert.issuer}
                          onChange={(e) => updateItem('certifications', cert.id, 'issuer', e.target.value)}
                          placeholder="Issuer / SETA"
                          className="w-36 h-10"
                        />
                        <Input
                          value={cert.year}
                          onChange={(e) => updateItem('certifications', cert.id, 'year', e.target.value)}
                          placeholder="Year"
                          className="w-20 h-10"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem('certifications', cert.id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <Input
                        value={cert.unit_standards || ''}
                        onChange={(e) => updateItem('certifications', cert.id, 'unit_standards', e.target.value)}
                        placeholder="Unit Standards (optional, e.g., US12345, US67890)"
                        className="h-9 text-sm bg-white"
                      />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-2">
                💡 For SETA qualifications, include unit standards - employers may verify these
              </p>
            </div>

            {/* Professional Body Memberships */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">Professional Body Memberships</h3>
                  <p className="text-sm text-slate-500">Add memberships to professional bodies or associations</p>
                </div>
                <Button
                  data-testid="add-membership-btn"
                  onClick={() => addItem('professional_memberships', { body_name: '', membership_number: '', membership_type: '', year_joined: '' })}
                  variant="outline"
                  className="border-slate-200"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
              {cvData.professional_memberships?.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <p className="text-slate-500 text-sm">No memberships added (optional)</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cvData.professional_memberships?.map((membership) => (
                    <div key={membership.id} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="flex items-center gap-3 mb-2">
                        <Input
                          value={membership.body_name}
                          onChange={(e) => updateItem('professional_memberships', membership.id, 'body_name', e.target.value)}
                          placeholder="Professional body (e.g., SACPLAN, ECSA, SAICA)"
                          className="flex-1 h-10"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem('professional_memberships', membership.id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          value={membership.membership_number || ''}
                          onChange={(e) => updateItem('professional_memberships', membership.id, 'membership_number', e.target.value)}
                          placeholder="Membership #"
                          className="h-9 text-sm bg-white"
                        />
                        <Select
                          value={membership.membership_type || ''}
                          onValueChange={(v) => updateItem('professional_memberships', membership.id, 'membership_type', v)}
                        >
                          <SelectTrigger className="h-9 text-sm bg-white">
                            <SelectValue placeholder="Type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Full Member">Full Member</SelectItem>
                            <SelectItem value="Associate">Associate</SelectItem>
                            <SelectItem value="Graduate">Graduate</SelectItem>
                            <SelectItem value="Student">Student</SelectItem>
                            <SelectItem value="Candidate">Candidate</SelectItem>
                            <SelectItem value="Fellow">Fellow</SelectItem>
                            <SelectItem value="Chartered">Chartered</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={membership.year_joined || ''}
                          onChange={(e) => updateItem('professional_memberships', membership.id, 'year_joined', e.target.value)}
                          placeholder="Year Joined"
                          className="h-9 text-sm bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-2">
                💡 Professional memberships show commitment to your field - SACPLAN for planners, ECSA for engineers, SAICA for accountants, etc.
              </p>
            </div>

            {/* References */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">References</h3>
                  <p className="text-sm text-slate-500">Add professional references</p>
                </div>
                <Button
                  data-testid="add-ref-btn"
                  onClick={() => addItem('references', { name: '', relationship: '', company: '', phone: '', email: '' })}
                  variant="outline"
                  className="border-slate-200"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
              {cvData.references.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <p className="text-slate-500 text-sm">No references added (optional)</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cvData.references.map((ref, index) => (
                    <div key={ref.id} id={`item-${ref.id}`} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-slate-700 text-sm">Reference {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem('references', ref.id)}
                          className="text-red-500 hover:text-red-600 h-8 w-8"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Input
                          value={ref.name}
                          onChange={(e) => updateItem('references', ref.id, 'name', e.target.value)}
                          placeholder="Name"
                          className="h-10"
                        />
                        <Input
                          value={ref.relationship}
                          onChange={(e) => updateItem('references', ref.id, 'relationship', e.target.value)}
                          placeholder="Relationship (e.g., Manager)"
                          className="h-10"
                        />
                        <Input
                          value={ref.company}
                          onChange={(e) => updateItem('references', ref.id, 'company', e.target.value)}
                          placeholder="Company"
                          className="h-10"
                        />
                        <Input
                          value={ref.phone}
                          onChange={(e) => updateItem('references', ref.id, 'phone', e.target.value)}
                          placeholder="Phone"
                          className="h-10"
                        />
                        <Input
                          value={ref.email}
                          onChange={(e) => updateItem('references', ref.id, 'email', e.target.value)}
                          placeholder="Email"
                          className="h-10 sm:col-span-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Hobbies & Interests */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">Hobbies & Interests</h3>
                  <p className="text-sm text-slate-500">Show your personality - SA employers value well-rounded candidates</p>
                </div>
                <Button
                  data-testid="add-hobby-btn"
                  onClick={() => {
                    setCvData(prev => ({
                      ...prev,
                      hobbies: [...(prev.hobbies || []), '']
                    }));
                  }}
                  variant="outline"
                  className="border-slate-200"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
              {(!cvData.hobbies || cvData.hobbies.length === 0) ? (
                <div className="text-center py-6 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <p className="text-slate-500 text-sm">No hobbies added (optional)</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cvData.hobbies.map((hobby, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Input
                        value={hobby}
                        onChange={(e) => {
                          const newHobbies = [...cvData.hobbies];
                          newHobbies[index] = e.target.value;
                          setCvData(prev => ({ ...prev, hobbies: newHobbies }));
                        }}
                        placeholder="e.g., Reading, Soccer, Volunteering, Photography"
                        className="flex-1 h-10"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          const newHobbies = cvData.hobbies.filter((_, i) => i !== index);
                          setCvData(prev => ({ ...prev, hobbies: newHobbies }));
                        }}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-2">
                💡 Include hobbies that show teamwork, leadership, or community involvement
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-600"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>
          <div className="hidden sm:block h-6 w-px bg-slate-200"></div>
          <h1 className="hidden sm:block font-semibold text-slate-900">
            {cvId ? 'Edit CV' : 'Create New CV'}
          </h1>
          {/* CV Completeness Score Badge */}
          <div className="hidden md:flex items-center gap-2 ml-4 px-3 py-1.5 rounded-full bg-slate-100">
            <div className={`w-2 h-2 rounded-full ${
              completeness.score >= 80 ? 'bg-emerald-500' : 
              completeness.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
            }`}></div>
            <span className="text-sm font-medium text-slate-700" data-testid="completeness-score">
              {completeness.score}% Complete
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => saveCV(false)}
            disabled={saving}
            className="border-slate-200"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          <Button
            variant="outline"
            onClick={() => setPreviewOpen(true)}
            className="border-slate-200"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          {cvId && (
            <Button
              onClick={handleDownload}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          )}
        </div>
      </header>

      {/* Progress Steps */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-white border-b border-slate-200 px-4 lg:px-8 py-3 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <button
                onClick={() => setCurrentStep(index)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  index === currentStep 
                    ? 'bg-slate-900 text-white' 
                    : index < currentStep 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-slate-100 text-slate-500'
                }`}
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  index < currentStep ? 'bg-emerald-500 text-white' : ''
                }`}>
                  {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                <span className="text-sm font-medium hidden lg:inline">{step.name}</span>
              </button>
              {index < steps.length - 1 && (
                <div className={`w-8 h-0.5 ${index < currentStep ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="pt-36 pb-24 px-4 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 lg:p-8">
            <div className="mb-6">
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {steps[currentStep].name}
              </h2>
              <p className="text-slate-500">{steps[currentStep].description}</p>
            </div>
            
            {renderStep()}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="border-slate-200"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            {currentStep < steps.length - 1 ? (
              <Button
                onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                data-testid="finish-cv-btn"
                onClick={() => saveCV(true)}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Finish & Save
              </Button>
            )}
          </div>

          {/* CV Completeness Checklist */}
          <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="completeness-checklist">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">CV Completeness</h3>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                completeness.score >= 80 ? 'bg-emerald-100 text-emerald-700' : 
                completeness.score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
              }`}>
                {completeness.score}%
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="h-2 bg-slate-100 rounded-full mb-4 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  completeness.score >= 80 ? 'bg-emerald-500' : 
                  completeness.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                }`}
                style={{ width: `${completeness.score}%` }}
              />
            </div>

            {completeness.score < 80 && (
              <p className="text-sm text-slate-500 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Complete these items for a stronger CV
              </p>
            )}

            <div className="grid sm:grid-cols-2 gap-2">
              {completeness.checks.map((check, idx) => (
                <div 
                  key={idx} 
                  className={`flex items-center gap-2 py-1.5 px-2 rounded-lg text-sm ${
                    check.done ? 'text-slate-500' : check.required ? 'bg-amber-50 text-amber-800' : 'text-slate-400'
                  }`}
                >
                  {check.done ? (
                    <CircleCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  ) : (
                    <Circle className={`w-4 h-4 flex-shrink-0 ${check.required ? 'text-amber-500' : 'text-slate-300'}`} />
                  )}
                  <span className={check.done ? 'line-through' : ''}>
                    {check.name}
                    {check.required && !check.done && <span className="text-red-500 ml-1">*</span>}
                  </span>
                </div>
              ))}
            </div>

            {completeness.score >= 80 && (
              <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-emerald-700 text-sm flex items-center gap-2">
                  <CircleCheck className="w-4 h-4" />
                  Your CV is ready for job applications!
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* CV Preview Modal */}
      <CVPreviewModal
        cv={cvData}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onDownload={handleDownload}
      />

      {/* Upgrade Modal for free users */}
      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        trigger={upgradeTrigger}
      />
    </div>
  );
};

export default CVBuilderPage;
